import { DOMAINS, type Domain, type LevelDefinition, type ProgressRepository } from "@/domain";
import { rankLevelsForExploration } from "@/features/progress/recommendation";
import type {
  IslandMapDecoration,
  IslandMapProgress,
  IslandMapRecommendation
} from "@/features/island-map";

export interface AppMapData {
  readonly dailyRecommendation?: IslandMapRecommendation | null;
  readonly decorations?: readonly IslandMapDecoration[];
  readonly progress?: readonly IslandMapProgress[];
}

export interface RepositoryMapData extends AppMapData {
  readonly preferredLevelIds: Readonly<Partial<Record<Domain, string>>>;
}

const DECORATION_PRESENTATION: Readonly<
  Record<
    string,
    Pick<IslandMapDecoration, "domain" | "kind" | "label">
  >
> = {
  "kite-garden": {
    domain: "focus-social",
    kind: "star",
    label: "风筝花园"
  },
  "shell-lantern": {
    domain: "chinese",
    kind: "sparkles",
    label: "贝壳灯"
  },
  "flower-arch": {
    domain: "habits",
    kind: "flower",
    label: "花朵拱门"
  },
  "harbor-windmill": {
    domain: "math",
    kind: "tree",
    label: "港湾风车"
  },
  "welcome-sailboat": {
    domain: "english",
    kind: "flag",
    label: "欢迎小帆船"
  }
};

export async function loadRepositoryMapData(
  repository: ProgressRepository,
  profileId: string,
  registry: readonly LevelDefinition[]
): Promise<RepositoryMapData> {
  const [attempts, mastery, rewards] = await Promise.all([
    repository.listAttempts(profileId),
    repository.getMastery(profileId),
    repository.getRewards(profileId)
  ]);
  const registeredLevels = new Map(registry.map((level) => [level.id, level]));
  const completedLevelIds = new Set(
    attempts.flatMap((attempt) => {
      const level = registeredLevels.get(attempt.levelId);
      return attempt.profileId === profileId && level?.domain === attempt.domain
        ? [attempt.levelId]
        : [];
    })
  );
  const rankedLevels = rankLevelsForExploration({
    attempts,
    levels: registry,
    mastery,
    profileId
  });
  const preferredLevelIds: Partial<Record<Domain, string>> = {};

  for (const rankedLevel of rankedLevels) {
    preferredLevelIds[rankedLevel.level.domain] ??= rankedLevel.level.id;
  }

  const firstRecommendation = rankedLevels[0]?.level ?? null;
  const decorations = rewards.unlockedDecorationIds.flatMap((id) => {
    const presentation = DECORATION_PRESENTATION[id];
    return presentation === undefined
      ? []
      : [{ id, ...presentation } satisfies IslandMapDecoration];
  });

  return {
    dailyRecommendation:
      firstRecommendation === null
        ? null
        : {
            domain: firstRecommendation.domain,
            levelId: firstRecommendation.id,
            title: firstRecommendation.title
          },
    decorations,
    preferredLevelIds,
    progress: DOMAINS.map((domain) => ({
      completedLevels: registry.filter(
        (level) =>
          level.domain === domain && completedLevelIds.has(level.id)
      ).length,
      domain,
      totalLevels: registry.filter((level) => level.domain === domain).length
    }))
  };
}

function compareLevels(
  left: LevelDefinition,
  right: LevelDefinition
): number {
  if (left.difficulty !== right.difficulty) {
    return left.difficulty - right.difficulty;
  }

  if (left.id < right.id) {
    return -1;
  }

  return left.id > right.id ? 1 : 0;
}

export function resolveRegisteredLevel(
  registry: readonly LevelDefinition[],
  levelId: string
): LevelDefinition | null {
  return registry.find((level) => level.id === levelId) ?? null;
}

export function resolveDomainLevelId(
  registry: readonly LevelDefinition[],
  domain: Domain,
  preferredLevelId?: string | null
): string | null {
  if (preferredLevelId !== undefined && preferredLevelId !== null) {
    const preferredLevel = resolveRegisteredLevel(registry, preferredLevelId);

    if (preferredLevel?.domain === domain) {
      return preferredLevel.id;
    }
  }

  let resolvedLevel: LevelDefinition | null = null;

  for (const level of registry) {
    if (
      level.domain === domain &&
      (resolvedLevel === null || compareLevels(level, resolvedLevel) < 0)
    ) {
      resolvedLevel = level;
    }
  }

  return resolvedLevel?.id ?? null;
}
